import { useState } from 'react';
import { configureSetup } from '@/services/driveApi';

interface SetupWizardProps {
  onConfigured: () => void;
}

export default function SetupWizard({ onConfigured }: SetupWizardProps) {
  const [rootPath, setRootPath] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = rootPath.trim();
    if (!trimmed) {
      setError('루트 폴더 경로를 입력해주세요');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await configureSetup(trimmed);
      setSuccess(true);
      setTimeout(() => {
        onConfigured();
      }, 1200);
    } catch (err: any) {
      setError(err.message || '설정 저장에 실패했습니다');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-accent-100/40 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-primary-100/40 blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-2xl p-8 border border-background-200 space-y-6">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-accent-100 flex items-center justify-center">
              <i className="ri-hard-drive-3-line text-2xl text-accent-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">WebDrive 설정</h1>
            <p className="text-sm text-foreground-600 leading-relaxed">
              파일이 저장될 루트 폴더 경로를 설정해주세요.<br />
              설정 후 모든 기능을 사용할 수 있습니다.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="root-path" className="block text-sm font-medium text-foreground-800 mb-1.5">
                저장소 루트 경로
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                  <i className="ri-folder-2-line text-sm"></i>
                </span>
                <input
                  id="root-path"
                  type="text"
                  value={rootPath}
                  onChange={(e) => {
                    setRootPath(e.target.value);
                    setError('');
                  }}
                  placeholder="/data/webdrive"
                  disabled={saving || success}
                  className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 disabled:opacity-50"
                  autoFocus
                />
              </div>
              <p className="mt-1.5 text-xs text-foreground-500">
                예: /data/webdrive &nbsp;|&nbsp; C:\Users\name\webdrive
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-100 text-primary-800 text-sm">
                <i className="ri-checkbox-circle-line flex-shrink-0"></i>
                <span>설정이 완료되었습니다! 잠시 후 이동합니다...</span>
              </div>
            )}

            <button
              type="submit"
              disabled={saving || success}
              className="w-full py-2.5 px-4 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors duration-150 whitespace-nowrap flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>설정 저장 중...</span>
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  <span>설정 저장</span>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-foreground-500 mt-4">
          서버의 config.json 파일에 저장됩니다. 언제든지 수정할 수 있습니다.
        </p>
      </div>
    </div>
  );
}